/**
 * @module app.cd
 */
import { autoinject, transient } from 'aurelia-framework';
import { Api } from 'app/shared/lib/api/api';
import { IApiQueryResults, IQueryLoadOptions, IApiActionResults } from 'app/shared/lib/api/api-types';
import { createQueryResults } from 'app/shared/lib/api/api-utilities';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { Toaster } from 'app/shared/lib/toaster';
import * as CD from 'app/cd/models/essette-api';

export interface IHouseholdSearchFilters {
    generalSearchTerm?: string | null;
    householdId?: string | null;
    householdName?: string | null;
    memberId?: string | null;
    medicaidId?: string | null;
    memberFirstName?: string | null;
    memberLastName?: string | null;
    memberDob?: string | null;
    memberEligibility?: boolean | null;
}

export function countHouseholdFilters(searchFilters: IHouseholdSearchFilters | null = null): number {
    if(!searchFilters) { return 0; }

    let count = 0;
    if(searchFilters.generalSearchTerm) { count += 1; }
    if(searchFilters.householdId) { count += 1; }
    if(searchFilters.householdName) { count += 1; }
    if(searchFilters.memberId) { count += 1; }
    if(searchFilters.medicaidId) { count += 1; }
    if(searchFilters.memberFirstName) { count += 1; }
    if(searchFilters.memberLastName) { count += 1; }
    if(searchFilters.memberDob) { count += 1; }
    if(!!searchFilters.memberEligibility) { count += 1; }
    return count;
}

@transient()
@autoinject()
export class HouseholdService {

    constructor(
        private readonly api: Api,
        private readonly logger: AppLogger,
        private readonly toaster: Toaster) {

        this.logger.source = this;
    }

    ////****************** Queries **************************************************////

    public async search(searchFilters: IHouseholdSearchFilters, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Household>> {
        if(countHouseholdFilters(searchFilters) == 0) {
            this.logger.warn('Invalid or missing search criteria');
            this.toaster.standard.searchNoCriteria('cd:household.$');
            return createQueryResults('CD.Household', [], true);
        }

        const query = this.api
            .query('cd/households')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(searchFilters.generalSearchTerm) {
            query.filterOr(query => query
                .filterString('identifier.primary', searchFilters.generalSearchTerm!, 'startsWith')
                .filterString('description', searchFilters.generalSearchTerm!, 'startsWith')
                .filterExists('memberships', (f =>
                    f.filterString('member.identifier.primary', searchFilters.generalSearchTerm!, 'startsWith')
                ))
                .filterExists('memberships', (f =>
                    f.filterString('member.name.first', searchFilters.generalSearchTerm!, 'startsWith')
                ))
                .filterExists('memberships', (f =>
                    f.filterString('member.name.last', searchFilters.generalSearchTerm!, 'startsWith')
                ))
            );
        } else {
            if(searchFilters.householdId) {
                query.filterString(
                    'identifier.primary',
                    searchFilters.householdId,
                    'startsWith');
            }
            if(searchFilters.householdName) {
                query.filterString(
                    'description',
                    searchFilters.householdName,
                    'startsWith');
            }
            if(searchFilters.memberId) {
                query.filterExists('memberships', (f =>
                    f.filterString('member.identifier.primary', searchFilters.memberId!, 'startsWith')
                ));
            }
            if(searchFilters.medicaidId) {
                query.filterExists('memberships', (f =>
                    f.filterString('member.identifier.medicaid', searchFilters.medicaidId!, 'startsWith')
                ));
            }
            if(searchFilters.memberFirstName) {
                query.filterExists('memberships', (f =>
                    f.filterString('member.name.first', searchFilters.memberFirstName!, 'startsWith')
                ));
            }
            if(searchFilters.memberLastName) {
                query.filterExists('memberships', (f =>
                    f.filterString('member.name.last', searchFilters.memberLastName!, 'startsWith')
                ));
            }
            if(searchFilters.memberDob) {
                query.filterExists('memberships', (f =>
                    f.filterDate('member.birthdate', searchFilters.memberDob!)
                ));
            }
            if(!!searchFilters.memberEligibility) {
                query.filterExists('memberships', (f =>
                    f.filterBoolean('member.eligibility.isCurrent', true)
                ));
            }
        }

        return await this.api.get(query);
    }


    ////****************** Loads ****************************************************////
    public async loadHousehold(householdId: string, includes: string[] = []): Promise<IApiQueryResults<CD.Household>> {
        const query = this.api
            .query(`cd/households/${householdId}`)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadHouseholdMembers(householdId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Member>> {
        const query = this.api
            .query(`cd/households/${householdId}/members`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(!options?.includeInactive) {
            query.filterBoolean('membership.isActive', true);
        }

        return await this.api.get(query);
    }

    ////****************** Actions **************************************************////
    public async saveHousehold(household: CD.Household): Promise<IApiActionResults> {
        const actionName = !household.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/households/${actionName === 'update' ? household.id : ''}`, household);

        return await this.api.post(action);
    }

    public async activateHouseholdMembership(membershipId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/households/memberships/${membershipId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }
}
